import * as React from 'react';
import { Tooltip, tokens } from '@fluentui/react-components';
import { Info16Regular, Checkmark16Filled } from '@fluentui/react-icons';
import type { WizardStep } from './Wizard';
import { stepLabelColor, stepMarkerStyle } from './stepMarker';

export interface WizardStepsProps {
  steps: WizardStep[];
  activeKey: string;
  completedByIndex: boolean[];
  onStepClick: (key: string) => void;
  canNavigateTo?: (key: string) => boolean;
  style?: React.CSSProperties;
}

const WizardSteps = ({
  steps,
  activeKey,
  completedByIndex,
  onStepClick,
  canNavigateTo,
  style,
}: WizardStepsProps) => {
  const activeIndex = steps.findIndex((s) => s.key === activeKey);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        padding: `0 ${tokens.spacingHorizontalXS}`,
        margin: `${tokens.spacingVerticalL} 0 ${tokens.spacingVerticalM}`,
        ...style,
      }}
    >
      {steps.map((step, index) => {
        const isActive = index === activeIndex;
        const navBlocked = !!canNavigateTo && !canNavigateTo(step.key);
        const isDisabled = !!step.disabled || navBlocked;
        const isCompleted = !!completedByIndex[index];
        const tooltip = step.disabled
          ? step.disabledTooltip
          : navBlocked
          ? step.lockedTooltip
          : undefined;
        return (
          <React.Fragment key={step.key}>
            <button
              type="button"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                flexShrink: 0,
                gap: tokens.spacingHorizontalS,
                background: 'none',
                border: 'none',
                padding: `${tokens.spacingVerticalXS} 0`,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.5 : 1,
              }}
              onClick={() => {
                if (isDisabled) return;
                onStepClick(step.key);
              }}
              disabled={isDisabled}
              aria-current={isActive ? 'step' : undefined}
            >
              <span style={stepMarkerStyle(isActive, isCompleted)}>
                {isCompleted ? <Checkmark16Filled /> : index + 1}
              </span>
              <span
                style={{
                  fontSize: tokens.fontSizeBase300,
                  whiteSpace: 'nowrap',
                  color: stepLabelColor(isActive),
                  fontWeight: tokens.fontWeightSemibold,
                }}
              >
                {step.label}
              </span>
            </button>
            {tooltip && (
              <Tooltip content={tooltip} relationship="description" withArrow>
                <button
                  type="button"
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    marginLeft: tokens.spacingHorizontalXS,
                  }}
                  aria-label={tooltip}
                >
                  <Info16Regular
                    style={{ color: tokens.colorNeutralForeground3 }}
                  />
                </button>
              </Tooltip>
            )}
            {index < steps.length - 1 && (
              <div
                style={{
                  flex: '0 1 64px',
                  minWidth: 16,
                  height: 2,
                  borderRadius: 2,
                  backgroundColor: tokens.colorNeutralStroke2,
                  margin: `0 ${tokens.spacingHorizontalM}`,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default WizardSteps;
